/**
 * FormRenderer — turns a FormSchema into a fully working form.
 *
 * Drives THREE consumers (one component, one mental model):
 *   1. Inflow form on a workflow's module-landing "+ Submit" button.
 *   2. Live preview pane in the FormBuilder (A3).
 *   3. Stage-gate required-fields modal (A5 — replaces TransitionModal's
 *      bespoke field map).
 *
 * Field types supported (Phase A):
 *   text, textarea, number, currency, date, datetime,
 *   select, multiselect, checkbox, file,
 *   picker (sub-types: user, property, account, truck)
 *
 * Conditional visibility: { show_if: { field, equals } }. Hidden fields
 * are NOT rendered AND NOT enforced as required. Server-side validator
 * mirrors this exactly.
 *
 * Props:
 *   schema           — FormSchema object (sections + fields). Required.
 *   values           — value-map { fieldKey: any }. Controlled.
 *   onChange         — (newValues) => void. Pure replace.
 *   files            — { fieldKey: File } for any file fields. Controlled.
 *   onFilesChange    — (newFiles) => void. Pure replace.
 *   errors           — { fieldKey: string } to render under fields.
 *   submitLabel      — string. The submit-button label. If null, no button rendered.
 *   onSubmit         — async fn. Called with (values, files). Errors thrown
 *                       are caught and stamped onto `errors` state.
 *   readOnly         — boolean. Disables every input.
 *   compact          — boolean. Tighter spacing for stage-gate modals.
 */
import React, { useMemo, useState, useRef } from 'react';
import { api } from '../../api';
import AccountSelector from '../AccountSelector';
import PropertySelector from '../PropertySelector';

// ─── Visual tokens ──────────────────────────────────────────────────
const COLORS = {
  brand: '#3F66A0',
  brandTint: '#eef3fb',
  border: '#e2e8f0',
  borderHover: '#cbd5e1',
  borderFocus: '#3F66A0',
  ink: '#0f172a',
  inkSoft: '#475569',
  inkMuted: '#94a3b8',
  red: '#dc2626',
  redTint: '#fef2f2',
  bg: '#fff',
};

const cardStyle = {
  background: COLORS.bg, borderRadius: 16, padding: '28px 32px',
  marginBottom: 20, border: `1px solid ${COLORS.border}`,
};

const compactCardStyle = {
  background: COLORS.bg, borderRadius: 12, padding: '20px 22px',
  marginBottom: 14, border: `1px solid ${COLORS.border}`,
};

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600,
  color: COLORS.inkSoft, marginBottom: 6,
};

// Splitting border into longhand props so re-renders that tweak only
// borderColor don't trip React's "mixing shorthand and longhand" warning.
const inputBase = {
  width: '100%', boxSizing: 'border-box', fontSize: 15,
  padding: '11px 14px',
  borderWidth: '1.5px', borderStyle: 'solid', borderColor: COLORS.border,
  borderRadius: 10, outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  background: '#fff', color: COLORS.ink, fontFamily: 'inherit',
};

// ─── Field-component map ────────────────────────────────────────────
function TextField({ field, value, onChange, error, readOnly }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      placeholder={field.placeholder || ''}
      maxLength={field.maxLength}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputBase, ...(error ? { borderColor: COLORS.red, background: COLORS.redTint } : null) }}
      onFocus={(e) => { if (!error) e.target.style.borderColor = COLORS.borderFocus; }}
      onBlur={(e) => { if (!error) e.target.style.borderColor = COLORS.border; }}
    />
  );
}

function TextareaField({ field, value, onChange, error, readOnly }) {
  return (
    <textarea
      value={value ?? ''}
      placeholder={field.placeholder || ''}
      maxLength={field.maxLength}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      style={{ ...inputBase, resize: 'vertical', minHeight: 96, lineHeight: 1.5,
        ...(error ? { borderColor: COLORS.red, background: COLORS.redTint } : null) }}
    />
  );
}

function NumberField({ field, value, onChange, error, readOnly, currency = false }) {
  return (
    <div style={{ position: 'relative' }}>
      {currency && (
        <span style={{
          position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
          color: COLORS.inkMuted, fontWeight: 600, fontSize: 15, pointerEvents: 'none',
        }}>$</span>
      )}
      <input
        type="number"
        value={value ?? ''}
        placeholder={field.placeholder || (currency ? '0.00' : '0')}
        min={field.min}
        max={field.max}
        step={currency ? '0.01' : 'any'}
        inputMode="decimal"
        readOnly={readOnly}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? '' : Number(v));
        }}
        style={{ ...inputBase, ...(currency ? { paddingLeft: 32 } : null),
          ...(error ? { borderColor: COLORS.red, background: COLORS.redTint } : null) }}
      />
    </div>
  );
}

function DateField({ field, value, onChange, error, readOnly }) {
  return (
    <input
      type="date"
      value={value ?? ''}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputBase, ...(error ? { borderColor: COLORS.red, background: COLORS.redTint } : null) }}
    />
  );
}

function DatetimeField({ field, value, onChange, error, readOnly }) {
  // value can be ISO; HTML datetime-local wants "YYYY-MM-DDTHH:MM"
  const local = (() => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  return (
    <input
      type="datetime-local"
      value={local}
      readOnly={readOnly}
      onChange={(e) => {
        if (!e.target.value) return onChange(null);
        onChange(new Date(e.target.value).toISOString());
      }}
      style={{ ...inputBase, ...(error ? { borderColor: COLORS.red, background: COLORS.redTint } : null) }}
    />
  );
}

function SelectField({ field, value, onChange, error, readOnly }) {
  const options = field.options || [];
  return (
    <select
      value={value ?? ''}
      disabled={readOnly}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...inputBase, cursor: readOnly ? 'not-allowed' : 'pointer',
        ...(error ? { borderColor: COLORS.red, background: COLORS.redTint } : null) }}
    >
      <option value="">— Select —</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function MultiselectField({ field, value, onChange, error, readOnly }) {
  const options = field.options || [];
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    if (readOnly) return;
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((o) => {
        const active = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => toggle(o.value)}
            disabled={readOnly}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
              cursor: readOnly ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
              border: active ? `2px solid ${COLORS.brand}` : `2px solid ${COLORS.border}`,
              background: active ? COLORS.brand : '#fff',
              color: active ? '#fff' : COLORS.inkSoft,
              ...(error ? { borderColor: COLORS.red } : null),
            }}
          >{o.label}</button>
        );
      })}
    </div>
  );
}

function CheckboxField({ field, value, onChange, readOnly }) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 10, cursor: readOnly ? 'not-allowed' : 'pointer',
      padding: '10px 14px', border: `1.5px solid ${COLORS.border}`, borderRadius: 10,
      background: value ? COLORS.brandTint : '#fff',
    }}>
      <input
        type="checkbox"
        checked={!!value}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, cursor: 'pointer', accentColor: COLORS.brand }}
      />
      <span style={{ fontSize: 14, color: COLORS.ink }}>{field.placeholder || 'Yes'}</span>
    </label>
  );
}

function FileField({ field, file, onChange, error, readOnly }) {
  const inputRef = useRef(null);
  const accept = field.accept || '';
  const hasFile = !!file;
  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={readOnly}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (!f) return;
          if (field.max_file_mb && f.size > field.max_file_mb * 1024 * 1024) {
            alert(`File too large. Max ${field.max_file_mb} MB.`);
            return;
          }
          onChange(f);
        }}
        style={{ display: 'none' }}
      />
      {!hasFile ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={readOnly}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            padding: '20px 22px', borderRadius: 12,
            border: `2px dashed ${error ? COLORS.red : COLORS.borderHover}`,
            background: error ? COLORS.redTint : '#fafbfc',
            color: COLORS.inkSoft, fontSize: 14, fontWeight: 600,
            cursor: readOnly ? 'not-allowed' : 'pointer', width: '100%',
          }}
        >
          <span style={{ fontSize: 22 }}>📎</span>
          <span>Click to upload{accept ? ` (${accept})` : ''}</span>
        </button>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderRadius: 10, border: `1.5px solid ${COLORS.border}`,
          background: '#fafbfc',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.ink, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {file.name}
            </div>
            <div style={{ fontSize: 12, color: COLORS.inkMuted }}>
              {(file.size / 1024).toFixed(1)} KB
            </div>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => onChange(null)}
              style={{ padding: '6px 12px', background: '#fff', border: `1px solid ${COLORS.border}`,
                color: COLORS.inkSoft, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >Replace</button>
          )}
        </div>
      )}
    </div>
  );
}

function PickerField({ field, value, onChange, readOnly }) {
  if (field.picker === 'property') {
    return (
      <PropertySelector
        value={value || null}
        onChange={(p) => onChange(p ? p.uuid || p.id : null)}
      />
    );
  }
  if (field.picker === 'account') {
    return (
      <AccountSelector
        value={value || null}
        onChange={(acct) => onChange(acct ? acct.id : null)}
      />
    );
  }
  if (field.picker === 'user') return <UserPicker value={value} onChange={onChange} readOnly={readOnly} />;
  if (field.picker === 'truck') return <TruckPicker value={value} onChange={onChange} readOnly={readOnly} />;
  return <div style={{ color: COLORS.inkMuted, fontSize: 13 }}>Unknown picker: {field.picker}</div>;
}

// Lazy local user-picker — there isn't a shared one yet. Loads the
// active-users list once on mount; small enough to render as a select.
function UserPicker({ value, onChange, readOnly }) {
  const [users, setUsers] = useState([]);
  React.useEffect(() => {
    api.getActiveUsers().then(list => setUsers(Array.isArray(list) ? list : [])).catch(() => setUsers([]));
  }, []);
  return (
    <select
      value={value ?? ''}
      disabled={readOnly}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      style={{ ...inputBase, cursor: readOnly ? 'not-allowed' : 'pointer' }}
    >
      <option value="">— Select user —</option>
      {users.map((u) => (
        <option key={u.id} value={u.id}>{u.full_name}</option>
      ))}
    </select>
  );
}

// Lazy truck picker — same shape as user picker. If trucks endpoint
// changes, this one place updates.
function TruckPicker({ value, onChange, readOnly }) {
  const [trucks, setTrucks] = useState([]);
  React.useEffect(() => {
    fetch('/api/maintenance/trucks?status=active', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(d => setTrucks(Array.isArray(d) ? d : (d.trucks || [])))
      .catch(() => setTrucks([]));
  }, []);
  return (
    <select
      value={value ?? ''}
      disabled={readOnly}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      style={{ ...inputBase, cursor: readOnly ? 'not-allowed' : 'pointer' }}
    >
      <option value="">— Select truck —</option>
      {trucks.map((t) => (
        <option key={t.id} value={t.id}>
          #{t.unit_number} {t.make ? `(${t.make}${t.model ? ' ' + t.model : ''})` : ''}
        </option>
      ))}
    </select>
  );
}

// ─── The renderer itself ────────────────────────────────────────────
const FIELD_RENDERERS = {
  text: TextField,
  textarea: TextareaField,
  number: NumberField,
  currency: (props) => <NumberField {...props} currency />,
  date: DateField,
  datetime: DatetimeField,
  select: SelectField,
  multiselect: MultiselectField,
  checkbox: CheckboxField,
  file: FileField,
  picker: PickerField,
};

/**
 * Decide if a field is currently visible given the values map.
 * Mirrors server-side formSchema.validateSubmission's show_if check.
 */
export function isFieldVisible(field, values) {
  if (!field.show_if) return true;
  return values[field.show_if.field] === field.show_if.equals;
}

export default function FormRenderer({
  schema,
  values,
  onChange,
  files = {},
  onFilesChange = () => {},
  errors: externalErrors = {},
  submitLabel = 'Submit',
  onSubmit,
  readOnly = false,
  compact = false,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');
  // Per-field errors: parent's externalErrors merged with our own client-side
  // checks. We own this so a failed submit immediately marks every missing
  // field red without forcing the parent to track validation state.
  const [internalErrors, setInternalErrors] = useState({});
  const errors = useMemo(
    () => ({ ...internalErrors, ...externalErrors }),
    [internalErrors, externalErrors],
  );

  const containerCardStyle = compact ? compactCardStyle : cardStyle;

  const fieldByKey = useMemo(() => {
    const m = {};
    for (const section of schema?.sections || []) {
      for (const field of section.fields || []) m[field.key] = field;
    }
    return m;
  }, [schema]);

  // Client-side pre-flight validation. Server is authoritative; this just
  // catches obvious misses fast and shows them in-line.
  const clientValidate = () => {
    const errs = {};
    for (const section of schema.sections) {
      for (const field of section.fields) {
        if (!isFieldVisible(field, values)) continue;
        const v = values[field.key];
        const empty = v === undefined || v === null || v === '' ||
                      (Array.isArray(v) && v.length === 0);
        if (field.required) {
          if (field.type === 'file') {
            if (!files[field.key]) errs[field.key] = 'Required';
          } else if (empty) {
            errs[field.key] = 'Required';
          }
        }
      }
    }
    return errs;
  };

  // Clear per-field error when the field is edited (better UX than
  // making the user re-submit just to clear the red).
  const setField = (key, v) => {
    onChange({ ...values, [key]: v });
    if (internalErrors[key]) {
      const next = { ...internalErrors };
      delete next[key];
      setInternalErrors(next);
    }
  };

  const setFile = (key, f) => {
    const next = { ...files };
    if (f) next[key] = f;
    else delete next[key];
    onFilesChange(next);
    if (internalErrors[key]) {
      const e = { ...internalErrors };
      delete e[key];
      setInternalErrors(e);
    }
  };

  const handleSubmit = async () => {
    if (!onSubmit) return;
    setTopError('');
    const clientErrs = clientValidate();
    if (Object.keys(clientErrs).length > 0) {
      setInternalErrors(clientErrs);
      setTopError('Please fill in all required fields.');
      return;
    }
    setInternalErrors({});
    setSubmitting(true);
    try {
      await onSubmit(values, files);
    } catch (e) {
      setTopError(e.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!schema || !Array.isArray(schema.sections)) {
    return (
      <div style={containerCardStyle}>
        <div style={{ color: COLORS.inkMuted, fontSize: 14 }}>No form schema loaded.</div>
      </div>
    );
  }

  return (
    <div>
      {schema.sections.map((section, sIdx) => {
        // Skip sections where every visible field is gone (avoids empty cards)
        const visibleFields = (section.fields || []).filter((f) => isFieldVisible(f, values));
        if (visibleFields.length === 0) return null;

        return (
          <section key={sIdx} style={containerCardStyle}>
            {section.title && (
              <h3 style={{ margin: 0, marginBottom: 4, fontSize: compact ? 14 : 17,
                fontWeight: 700, color: COLORS.ink, letterSpacing: '-0.01em' }}>
                {section.title}
              </h3>
            )}
            {section.description && (
              <p style={{ margin: '0 0 18px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                {section.description}
              </p>
            )}

            <div style={{ display: 'grid', gap: compact ? 14 : 18 }}>
              {visibleFields.map((field) => {
                const Renderer = FIELD_RENDERERS[field.type];
                const fieldError = errors[field.key];
                return (
                  <div key={field.key}>
                    {field.type !== 'checkbox' && (
                      <label style={labelStyle}>
                        {field.label}
                        {field.required && <span style={{ color: COLORS.red, marginLeft: 4 }}>*</span>}
                      </label>
                    )}
                    {Renderer ? (
                      field.type === 'file' ? (
                        <FileField
                          field={field}
                          file={files[field.key]}
                          onChange={(f) => setFile(field.key, f)}
                          error={fieldError}
                          readOnly={readOnly}
                        />
                      ) : (
                        <Renderer
                          field={field}
                          value={values[field.key]}
                          onChange={(v) => setField(field.key, v)}
                          error={fieldError}
                          readOnly={readOnly}
                        />
                      )
                    ) : (
                      <div style={{ color: COLORS.red, fontSize: 13 }}>
                        Unknown field type: {field.type}
                      </div>
                    )}
                    {field.help && !fieldError && (
                      <div style={{ fontSize: 12, color: COLORS.inkMuted, marginTop: 6 }}>{field.help}</div>
                    )}
                    {fieldError && (
                      <div style={{ fontSize: 12, color: COLORS.red, marginTop: 6, fontWeight: 600 }}>
                        {fieldError}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {topError && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: '#fef3c7', border: '1px solid #fcd34d',
          color: '#78350f', fontSize: 13, fontWeight: 600,
        }}>
          {topError}
        </div>
      )}

      {submitLabel && onSubmit && !readOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '14px 28px', borderRadius: 12, fontSize: 15, fontWeight: 700,
              border: 'none', cursor: submitting ? 'wait' : 'pointer',
              background: submitting ? '#cbd5e1' : COLORS.brand, color: '#fff',
              transition: 'all 0.15s',
              boxShadow: submitting ? 'none' : '0 2px 6px rgba(63,102,160,0.25)',
            }}
          >
            {submitting ? 'Submitting…' : submitLabel}
          </button>
        </div>
      )}
    </div>
  );
}
